import { useEffect, useRef } from 'react';

export default function Hero() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.style.opacity = '1';
        titleRef.current.style.transform = 'translateY(0)';
      }
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0F0F0F 0%, #0D1B2A 50%, #0F0F0F 100%)' }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=luxurious%20dark%20law%20office%20interior%20with%20deep%20mahogany%20wood%20paneling%20and%20rich%20leather%20furniture%2C%20dramatic%20chiaroscuro%20lighting%20with%20warm%20golden%20lamp%20glow%2C%20sophisticated%20legal%20library%20with%20leather-bound%20books%2C%20polished%20marble%20floor%20reflecting%20golden%20light%2C%20high-end%20prestigious%20ambiance%2C%20cinematic%20depth%20of%20field%2C%20dark%20navy%20and%20charcoal%20tones%20with%20warm%20amber%20highlights%2C%20architectural%20columns%20and%20formal%20drapery&width=1920&height=1080&seq=hero01&orientation=landscape"
          alt="Benner Martins Advocacia - Escritório de Advocacia Especializada"
          className="w-full h-full object-cover object-top opacity-25"
        />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-deep/95 via-deep/80 to-deep/40" />

      {/* Gold Line Decoration */}
      <div className="absolute right-0 top-1/4 h-3/5 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent z-10" />
      <div className="absolute right-12 top-1/3 h-2/5 w-px bg-gradient-to-b from-transparent via-gold/20 to-transparent z-10" />

      {/* Pattern Overlay */}
      <div
        className="absolute inset-0 z-0 opacity-5"
        style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 80px,
            #C8A24A 80px,
            #C8A24A 81px
          )`,
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-[132px] lg:pt-20 pb-20 lg:pb-0">
        <div
          ref={titleRef}
          style={{ opacity: 0, transform: 'translateY(40px)', transition: 'opacity 0.9s ease, transform 0.9s ease' }}
          className="max-w-3xl"
        >
          {/* Tag */}
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold font-sans font-600 text-xs tracking-[0.25em] uppercase">
              Advocacia Especializada
            </span>
          </div>

          {/* Main Title */}
          <h1 className="font-serif text-ice leading-tight mb-3">
            <span className="block text-5xl lg:text-7xl font-700">Benner Martins</span>
            <span className="block text-2xl lg:text-3xl font-300 text-ice/70 mt-2 tracking-wide">
              Advocacia Especializada
            </span>
          </h1>

          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <span className="w-16 h-px bg-gold" />
            <span className="w-2 h-2 rounded-full bg-gold" />
            <span className="w-8 h-px bg-gold/50" />
          </div>

          {/* Subtitle */}
          <p className="font-sans text-lg lg:text-xl font-300 text-ice/75 leading-relaxed max-w-xl mb-12">
            Defesa estratégica e atuação jurídica especializada com excelência — Direito Penal e Direito Previdenciário com técnica, ética e comprometimento.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://wa.me/5598981578332?text=Olá,%20gostaria%20de%20falar%20com%20um%20advogado."
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-deep font-sans font-600 text-base px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ boxShadow: '0 0 32px rgba(200,162,74,0.3)' }}
            >
              <i className="ri-whatsapp-line text-lg" />
              Fale com um Advogado
            </a>
            <button
              onClick={() => handleScroll('#areas')}
              className="inline-flex items-center justify-center gap-2 border border-gold/50 hover:border-gold text-gold hover:text-gold-light font-sans font-500 text-base px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer bg-transparent"
            >
              Áreas de Atuação
              <i className="ri-arrow-right-line" />
            </button>
          </div>

          {/* Info Strip */}
          <div className="flex flex-wrap gap-6 mt-14 pt-8 border-t border-gold/15">
            <div className="flex items-center gap-2 text-ice/50 font-sans text-sm">
              <i className="ri-map-pin-line text-gold text-sm" />
              <span>São Luís — MA</span>
            </div>
            <div className="flex items-center gap-2 text-ice/50 font-sans text-sm">
              <i className="ri-scales-3-line text-gold text-sm" />
              <span>OAB/MA Registrado</span>
            </div>
            <div className="flex items-center gap-2 text-ice/50 font-sans text-sm">
              <i className="ri-shield-check-line text-gold text-sm" />
              <span>Atendimento Especializado</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
